/**
 * fundingSummary — pure helpers for the "What it means over time" narrative beat.
 *
 * Turns a projection array into a plain-language story of how a cashflow deficit is funded
 * across the plan (bank account first, then the next liquid source), and when a source runs
 * low. Shared by every module's insight panel so the future-funding explanation is consistent.
 *
 * No React, no DOM, no app state — easy to unit-test.
 */

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';

/** One balance-holding row (e.g. a bank account or shares asset) — only the field this module reads. */
interface ProjectionAssetRow {
    value?: number | string;
}

/**
 * One projection year, as returned by the calc engine (server/src/rest/calculation/*). Loosely
 * typed — the engine's projection response isn't modelled in shared/factFindTypes.ts (see
 * calculationApi.ts's own doc comment), and this helper only ever reads a handful of fields off it.
 */
/** One line of the engine's "where the shortfall came from" breakdown for a year. */
export interface FundingDrawdownRow {
    label?: string | null;
    /** The engine's stable source name. See {@link isBorrowingRow}. */
    tag?: string | null;
}

export interface FundingProjectionYear {
    year?: number;
    cashFlow?: number;
    netCashflow?: number;
    drawdownBreakdown?: FundingDrawdownRow[];
    /** The engine's liabilities for the year; only the shortfall loan's rate is read here. */
    liabilities?: { helocDebt?: { interestRate?: number | string | null }[] };
    assets?: {
        bankAccounts?: ProjectionAssetRow[];
        accountBasedPensions?: ProjectionAssetRow[];
        shares?: ProjectionAssetRow[];
        [key: string]: ProjectionAssetRow[] | undefined;
    };
}

/**
 * Is this drawdown row the borrowing-against-the-home line?
 *
 * The engine stamps `tag: 'heloc'` on it. The label fallbacks exist because saved strategies and
 * stored projections from before 2026-08-21 carry the old wording ("HELOC Debt Created"), and
 * without them those older files would silently lose the disclosure that the plan runs on debt.
 *
 * Deliberately NOT a loose "mentions the home" test: a reverse mortgage and the Home Equity Access
 * Scheme are also secured on the home, are modelled separately, and must not be swallowed by this.
 */
export function isBorrowingRow(row: FundingDrawdownRow | null | undefined): boolean {
    if (!row) return false;
    if (row.tag === 'heloc') return true;
    const label = (row.label || '').toLowerCase();
    return label.includes('heloc') || label.includes('borrowing against the home') || label.includes('borrowed against the home');
}

/**
 * Is this drawdown row the deposit line — care fees drawn from the refundable accommodation deposit?
 *
 * The engine stamps `tag: 'rad'` (radPlugin.ts, 2026-09-12). The label fallback is for the same
 * reason as {@link isBorrowingRow}'s: a stored projection outlives the wording.
 */
export function isRadDrawdownRow(row: FundingDrawdownRow | null | undefined): boolean {
    if (!row) return false;
    if (row.tag === 'rad') return true;
    return (row.label || '').toLowerCase().includes('refundable accommodation deposit');
}

/** What the runway copy calls the deposit when it is the next source. */
const RAD_SOURCE = 'refundable accommodation deposit';

const sumBalances = (list: ProjectionAssetRow[] | undefined): number =>
    (list || []).reduce((s, a) => s + (parseFloat(String(a && a.value)) || 0), 0);

// Aged Care / SAH projection rows expose annual cashflow as `cashFlow`; Retirement Village rows use
// `netCashflow` (the village-adjusted figure). Read whichever is present so one helper serves all modules.
const cashFlowOf = (p: FundingProjectionYear): number => (p && (p.cashFlow ?? p.netCashflow)) || 0;

interface FundingSummary {
    hasData: boolean;
    hasDeficit?: boolean;
    totalYears?: number;
    firstDeficitYear?: number | null;
    bankDepletesYear?: number | null;
    nextSource?: string | null;
    fundedFullPlan?: boolean;
    coveredPct?: number;
    /**
     * The first projected year in which the plan borrows against the home, or null if it never does.
     *
     * This is the question a client actually asks and the tool could not previously answer. From an
     * adviser's meeting notes, 2026-08-21: with $400k of cash and $350k of it going into the room
     * deposit, the client "immediately picked up ... how would he be able to fund the room once the
     * cash ran out - there was no explanation of this either on screen or in the insight panel".
     */
    borrowingStartsYear?: number | null;
    /**
     * The rate the engine's own reverse mortgage accrues at, read off the projection so the copy
     * quotes the figure actually modelled. Falls back to the shared default, which is what the engine
     * uses when nothing was recorded.
     */
    borrowingRate?: number;
    /**
     * The first year care fees are drawn from the refundable accommodation deposit, or null if never.
     * Comes before borrowing in the waterfall: the deposit is the client's own money.
     */
    radDrawStartsYear?: number | null;
    /**
     * The first year the engine could not fund the shortfall from ANYTHING — every pool drained and
     * no home equity left to borrow against. This is the engine's own `unfundedShortfall` turning
     * positive, which its author called "the year the client's money runs out".
     *
     * Distinct from `bankDepletesYear`, which is only the first pool emptying, and from
     * `borrowingStartsYear`, where the money has not run out so much as changed into a debt.
     */
    moneyRunsOutYear?: number | null;
}

/** One projection row's unfunded shortfall, whatever the engine called it. */
const unfundedOf = (p: FundingProjectionYear): number =>
    Number((p as { unfundedShortfall?: number }).unfundedShortfall) || 0;

/** Structured summary derived from the projection years. */
export function summariseFunding(projections: FundingProjectionYear[] | null | undefined): FundingSummary {
    const years = Array.isArray(projections) ? projections : [];
    const totalYears = years.length;
    if (totalYears === 0) return { hasData: false };

    let firstDeficitYear: number | null = null;
    let bankDepletesYear: number | null = null;
    let moneyRunsOutYear: number | null = null;
    years.forEach((p, i) => {
        const yr = (p && p.year) || i + 1;
        if (cashFlowOf(p) < 0 && firstDeficitYear === null) firstDeficitYear = yr;
        if (bankDepletesYear === null && sumBalances(p && p.assets && p.assets.bankAccounts) < 1000) bankDepletesYear = yr;
        if (moneyRunsOutYear === null && unfundedOf(p) > 0.5) moneyRunsOutYear = yr;
    });

    const hasDeficit = firstDeficitYear !== null;

    // The first year the plan borrows against the home, read off the engine's own drawdown rows
    // rather than inferred from balances — borrowing is an event, not a balance.
    let borrowingStartsYear: number | null = null;
    years.forEach((p, i) => {
        if (borrowingStartsYear !== null) return;
        if ((p?.drawdownBreakdown || []).some(isBorrowingRow)) borrowingStartsYear = (p && p.year) || i + 1;
    });

    // The deposit is drawn before anything is borrowed, so this is the earlier of the two events.
    let radDrawStartsYear: number | null = null;
    years.forEach((p, i) => {
        if (radDrawStartsYear !== null) return;
        if ((p?.drawdownBreakdown || []).some(isRadDrawdownRow)) radDrawStartsYear = (p && p.year) || i + 1;
    });

    // The rate the loan is actually accruing at, from the first year that carries the balance.
    let borrowingRate = REVERSE_MORTGAGE_DEFAULT_RATE;
    for (const p of years) {
        const recorded = parseFloat(String(p?.liabilities?.helocDebt?.[0]?.interestRate ?? ''));
        if (Number.isFinite(recorded) && recorded > 0) {
            borrowingRate = recorded;
            break;
        }
    }

    // What funds the deficit once the bank runs out — first non-trivial liquid source after that
    // point, and failing that, borrowing. Borrowing is checked LAST because it is the engine's own
    // last resort: it only lends once bank, pension and shares are exhausted.
    let nextSource: string | null = null;
    if (bankDepletesYear !== null) {
        const depletesYear: number = bankDepletesYear;
        const after = years.find((p, i) => ((p && p.year) || i + 1) >= depletesYear) || ({} as FundingProjectionYear);
        if (sumBalances(after.assets && after.assets.accountBasedPensions) > 1000) nextSource = 'account-based pension';
        else if (sumBalances(after.assets && after.assets.shares) > 1000) nextSource = 'shares';
        else if (radDrawStartsYear !== null) nextSource = RAD_SOURCE;
        else if (borrowingStartsYear !== null) nextSource = 'borrowing against the home';
    }

    const fundedFullPlan = !hasDeficit || bankDepletesYear === null;
    const coveredPct = fundedFullPlan
        ? 100
        : Math.max(4, Math.round((Math.max(0, (bankDepletesYear ?? 0) - 1) / totalYears) * 100));

    return { hasData: true, hasDeficit, totalYears, firstDeficitYear, bankDepletesYear, nextSource, fundedFullPlan, coveredPct, borrowingStartsYear, borrowingRate, radDrawStartsYear, moneyRunsOutYear };
}

/**
 * The plan summary panel's "How long the money lasts", worded for a narrow pane.
 *
 * WHY A SECOND COPY FUNCTION. `fundingRunwayCopy` below speaks in terms of "your N-year plan",
 * which is right where N is the period the client chose. This block deliberately runs on a LONGER,
 * fixed horizon than the plan period, so that phrasing would be wrong ("your 25-year plan" when the
 * client set five) and, worse, misleading in the other direction: asked over five years the helper
 * answers "lasts your full 5-year plan" for a household whose savings actually run low in year 13,
 * because it can only ever see the years it is given. A missing number is obvious; a falsely
 * comforting sentence is not. So this one always says what it was tested over.
 *
 * The FACTS still come from `summariseFunding` — one source, two wordings, which is the same
 * arrangement the walk-in note and the plan recap already have.
 */
export interface PlanRunwayCopy {
    /** "Savings last about 13 years", or the no-deficit line. */
    headline: string;
    coveredPct: number;
    leftLabel: string;
    rightLabel: string;
    depleted: boolean;
    /** The borrowing warning, or null. Amber is earned here: it is a debt that grows. */
    flag: string | null;
    /** Always stated, so the runway can never be read without knowing what it was tested over. */
    basis: string;
    /**
     * The year the savings are exhausted, or null when they are not within the horizon.
     *
     * EXPOSED SO NOTHING ELSE HAS TO DIVIDE (owner-caught, 2026-09-10). The walk-in note used to
     * work its own runway out as `accessible savings ÷ yearly gap` — straight-line arithmetic that
     * ignores investment growth, the pension moving, care fees changing and account-based pension
     * minimums. On one production client that produced "roughly 12 years" beside this panel's
     * "about 10 years", for the same untouched position, on the same screen.
     *
     * Both were honestly derived; only one was the projection's answer. Handing that year out means
     * every surface can quote it instead of estimating one of its own.
     */
    exhaustedYear: number | null;
    /**
     * The facts behind the flag, exposed so the plan summary rail can say them in step 2's words
     * (see moneySequence.tsx) without deriving a second set of years of its own.
     */
    bankDepletesYear: number | null;
    radDrawStartsYear: number | null;
    borrowingStartsYear: number | null;
}

export function planRunwayCopy(
    projections: FundingProjectionYear[] | null | undefined,
    horizonYears: number,
): PlanRunwayCopy | null {
    const s = summariseFunding(projections);
    if (!s.hasData) return null;
    const basis = `Tested over ${horizonYears} years.`;

    if (!s.hasDeficit) {
        return {
            headline: 'Cashflow stays positive — no savings are drawn down',
            coveredPct: 100,
            leftLabel: 'now',
            rightLabel: 'no gap to fund',
            depleted: false,
            flag: null,
            basis,
            exhaustedYear: null,
            bankDepletesYear: null,
            radDrawStartsYear: null,
            borrowingStartsYear: null,
        };
    }

    const { bankDepletesYear, borrowingStartsYear, borrowingRate, radDrawStartsYear, moneyRunsOutYear, nextSource, totalYears = horizonYears } = s;

    // Said whenever the deposit is drawn on, because it changes two figures a client cares about —
    // the refund at the end and the daily accommodation payment — and neither shows up in cashflow.
    const radNote =
        radDrawStartsYear != null
            ? ` From about year ${radDrawStartsYear} the care fees are drawn from the refundable accommodation deposit, which reduces the refund when care ends and raises the daily accommodation payment.`
            : '';

    /*
     * "SAVINGS" MEANS ALL SAVINGS (owner-caught, 2026-08-26). This headline first read
     * `bankDepletesYear`, which is only the first pool emptying — the BANK ACCOUNT falling below
     * $1,000. For a household with $165,000 in the bank and $500,000 in an account-based pension
     * that produced "Savings last about 4 years" on the same screen as the walk-in note's "would
     * cover that for roughly 9 years". Both were right about different things and the screen looked
     * broken, which is the worst outcome of the two.
     *
     * The runway is therefore the year the savings are ACTUALLY exhausted: the year borrowing
     * begins (the engine only lends once bank, pension and shares are gone) or, where there is
     * nothing to borrow against, the year the engine could fund nothing at all. Which pool empties
     * first is real but secondary, and is said underneath rather than in the headline.
     */
    const exhaustedYear = borrowingStartsYear ?? moneyRunsOutYear;

    if (exhaustedYear == null) {
        return {
            headline: `Savings last beyond ${horizonYears} years`,
            coveredPct: 100,
            leftLabel: 'now',
            rightLabel: `still funded at year ${horizonYears}`,
            depleted: false,
            flag: bankDepletesYear != null && nextSource
                ? `The bank account runs low around year ${bankDepletesYear}; from then the shortfall comes from the ${nextSource}.${nextSource === RAD_SOURCE ? ' Each dollar drawn reduces the refund when care ends and raises the daily accommodation payment.' : ''}`
                : null,
            basis,
            exhaustedYear: null,
            bankDepletesYear: bankDepletesYear ?? null,
            radDrawStartsYear: radDrawStartsYear ?? null,
            borrowingStartsYear: null,
        };
    }

    const years = exhaustedYear === 1 ? 'about a year' : `about ${exhaustedYear} years`;
    const borrows = borrowingStartsYear != null;
    // The first pool to empty, mentioned only when it is genuinely earlier than the headline year.
    const firstPoolNote =
        bankDepletesYear != null && bankDepletesYear < exhaustedYear && nextSource
            ? ` The bank account runs low around year ${bankDepletesYear}; the ${nextSource} carries it from there.`
            : '';
    return {
        headline: `Savings last ${years}`,
        coveredPct: Math.max(4, Math.round((Math.max(0, exhaustedYear - 1) / totalYears) * 100)),
        leftLabel: 'now',
        rightLabel: `savings gone ~year ${exhaustedYear}`,
        depleted: true,
        // Borrowing gets the warning, not merely a mention: it is a debt that grows against the
        // home until the home is sold, which is a different thing from spending another account.
        flag: borrows
            ? `From about year ${borrowingStartsYear} the plan borrows against the home through a reverse mortgage at about ${borrowingRate}% — nothing is repaid, so that debt grows each year until the home is sold, and it reduces what is left at the end.${radNote}${firstPoolNote}`
            : `From about year ${exhaustedYear} there is nothing accessible left to cover the shortfall — worth planning another source.${radNote}${firstPoolNote}`,
        basis,
        exhaustedYear,
        bankDepletesYear: bankDepletesYear ?? null,
        radDrawStartsYear: radDrawStartsYear ?? null,
        borrowingStartsYear: borrowingStartsYear ?? null,
    };
}

export interface FundingRunwayCopy {
    hasDeficit: boolean;
    headline: string;
    coveredPct: number;
    leftLabel: string;
    rightLabel: string;
    depleted: boolean;
    flag: string | null;
}

/** Display-ready copy for the runway component (headline, labels, amber flag) — calm by default. */
export function fundingRunwayCopy(projections: FundingProjectionYear[] | null | undefined): FundingRunwayCopy | null {
    const s = summariseFunding(projections);
    if (!s.hasData) return null;
    const { hasDeficit, totalYears = 0, firstDeficitYear, bankDepletesYear, nextSource, fundedFullPlan, coveredPct = 100, borrowingStartsYear, borrowingRate, radDrawStartsYear } = s;

    if (!hasDeficit) {
        return { hasDeficit: false, headline: `Your cashflow stays positive across your ${totalYears}-year plan — no savings drawdown needed.`,
            coveredPct: 100, leftLabel: `${totalYears}-year plan`, rightLabel: 'no gap', depleted: false, flag: null };
    }
    // Lead differs when the first years are a surplus and the shortfall only appears later.
    const lead = (firstDeficitYear ?? 0) > 1
        ? `Your cashflow starts positive, then turns to a shortfall around Year ${firstDeficitYear}. From then it's `
        : `Each year's shortfall is `;
    if (fundedFullPlan) {
        return { hasDeficit: true, headline: `${lead}drawn from your bank account, which lasts your full ${totalYears}-year plan.`,
            coveredPct: 100, leftLabel: 'bank account', rightLabel: 'covers the full plan', depleted: false, flag: null };
    }
    const base = { hasDeficit: true, headline: `${lead}drawn from your bank account, which runs low around Year ${bankDepletesYear} of ${totalYears}.`,
        coveredPct, leftLabel: 'bank account', rightLabel: `runs low ~Year ${bankDepletesYear}`, depleted: true };
    // Borrowing gets its own sentence rather than being listed like a savings account: it is a debt
    // that grows, and a client reading "funded from borrowing against the home" needs to be told
    // what that costs them, not merely where the money appears from.
    if (nextSource === 'borrowing against the home') {
        return { ...base, flag: `After about Year ${borrowingStartsYear ?? bankDepletesYear} the savings no longer cover the shortfall, so the plan borrows against the home through a reverse mortgage at about ${borrowingRate}%. Nothing is repaid, so that debt grows each year until the home is sold, and it reduces what is left at the end.` };
    }
    // The deposit is the client's own money, but drawing it changes two figures cashflow never shows:
    // the refund at the end, and the accommodation payment from the following year.
    if (nextSource === RAD_SOURCE) {
        const then = radDrawStartsYear != null && borrowingStartsYear != null
            ? ` Once the deposit is used up (around Year ${borrowingStartsYear}) the plan borrows against the home through a reverse mortgage at about ${borrowingRate}%.`
            : '';
        return { ...base, flag: `After about Year ${radDrawStartsYear ?? bankDepletesYear} the care fees are drawn from your refundable accommodation deposit. Each dollar drawn reduces the refund when care ends and raises the daily accommodation payment.${then}` };
    }
    return { ...base, flag: nextSource
        ? `After about Year ${bankDepletesYear}, the shortfall is then funded from your ${nextSource}.`
        : `After about Year ${bankDepletesYear} there aren't enough easily-accessible savings to cover the shortfall — worth planning another source (for example, selling an asset).` };
}
